import { StyleSheet, Text, View } from "@react-pdf/renderer";
import TableColPdf from "components/pdf/TableColPdf";
import { Quotation } from "features/quotation/types";
import { formatCurrency, isPriceLabel } from "utils";
import BarcodePdf from "components/pdf/BarcodePdf";
import { useMemo } from "react";

/** Render price with ¥ on the left and value centered in the cell for PDF. When styleContent has fontSize, ¥ uses the same size. */
function renderPriceWithYen(
  value: string,
  styleContent?: { fontSize?: number } & Record<string, unknown>,
  prependMinus?: boolean
) {
  const valueStyle = { fontSize: 7, flex: 1, textAlign: "center" as const, ...styleContent };
  const yenFontSize = typeof valueStyle.fontSize === "number" ? valueStyle.fontSize : 7;
  const displayValue = prependMinus ? `-${value}` : value;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
        paddingHorizontal: 4,
        width: "100%",
      }}
    >
      <Text style={{ fontSize: yenFontSize }}>¥</Text>
      <Text style={valueStyle}>{displayValue}</Text>
      {/* Empty view to balance the ¥ symbol for true center alignment */}
      <View style={{ width: 7 }} />
    </View>
  );
}

const styles = StyleSheet.create({
  estRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 4,
    borderBottom: "1px solid #707070",
    gap: 1,
  },
  estRowItem: {
    height: 30,
  },
  layoutFooter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  textFooter: {
    fontSize: 8,
  },
});

/**
 * TableEstPreviewPdf component renders a table with a label and optional sx prop for styling.
 *
 * @param {Object} root0 - The props object.
 * @param {Quotation} root0.quotation - The quotation data to display in the table.
 */
export default function TableEstPreviewPdf({ quotation }: { quotation: Quotation }) {
  const cashback = useMemo(() => {
    if (!quotation.cb_tire_price && !quotation.cb_wheel_price) return "-";
    return formatCurrency((quotation.cb_tire_price ?? 0) + (quotation.cb_wheel_price ?? 0));
  }, [quotation.cb_tire_price, quotation.cb_wheel_price]);

  const discountAmount = useMemo(() =>
    (quotation.confirm_total_price ?? 0) - (
      (quotation.discounted_sale_price ?? 0) +
      (quotation.wage_cost_price ?? 0) +
      (quotation.puncture_protection_price ?? 0)
    )
  , [quotation.confirm_total_price, quotation.discounted_sale_price, quotation.wage_cost_price, quotation.puncture_protection_price]);

  // Price column values for ¥ + value with space-between
  const nomalVal = formatCurrency(quotation.nomal_price || 0);
  const discountedVal = formatCurrency(quotation.discounted_sale_price || 0);
  const wageVal = formatCurrency(quotation.wage_cost_price || 0);
  const punctureVal = formatCurrency(quotation.puncture_protection_price || 0);
  const discountVal = formatCurrency(discountAmount !== 0 ? discountAmount : 0);
  const totalVal = formatCurrency(quotation.confirm_total_price || 0);

  return (
    <View>
      <View style={styles.estRow}>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 140 }}
          title="メーカー/商品名/サイズ"
          content={`${quotation.maker_name || ""}\n${quotation.item_name || ""}`}
          styleContent={{ textAlign: "left" }}
          styleTitle={{ textAlign: "left", width: "100%", paddingLeft: 2 }}
        />
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 65 }}
          title="LI / 速度記号"
          content={quotation.tire_loin_speed_mark || ""}
        />
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 70 }}
          title="ラベリング"
          content={quotation.tire_label || ""}
        />
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 27 }}
          title="本数"
          content={quotation.quantity ? `${quotation.quantity}本` : ""}
        />
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 50 }}
          title="通常価格"
          content={!isPriceLabel(nomalVal) ? nomalVal : undefined}
        >
          {isPriceLabel(nomalVal) ? renderPriceWithYen(nomalVal) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 40 }}
          title="割引率"
          content={
            quotation.special_discount
              ? `${quotation.special_discount}%`
              : `${quotation.fixed_discount || 0}%`
          }
        />
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 50 }}
          title="割引後価格"
          content={!isPriceLabel(discountedVal) ? discountedVal : undefined}
        >
          {isPriceLabel(discountedVal) ? renderPriceWithYen(discountedVal, undefined, true) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 55 }}
          title="工賃"
          content={!isPriceLabel(wageVal) ? wageVal : undefined}
        >
          {isPriceLabel(wageVal) ? renderPriceWithYen(wageVal) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 55 }}
          title="パンク補償価格"
          content={!isPriceLabel(punctureVal) ? punctureVal : undefined}
        >
          {isPriceLabel(punctureVal) ? renderPriceWithYen(punctureVal) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ width: 55 }}
          title="値引き"
          styleTitle={{ fontSize: 7, letterSpacing: -1 }}
          content={!isPriceLabel(discountVal) ? discountVal : undefined}
        >
        {isPriceLabel(discountVal) ? renderPriceWithYen(discountVal) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ flex: 1 }}
          title="見積金額"
          content={!isPriceLabel(totalVal) ? totalVal : undefined}
        >
          {isPriceLabel(totalVal) ? renderPriceWithYen(totalVal, { fontSize: 10 }) : null}
        </TableColPdf>
        <TableColPdf
          styleBoxTitle={styles.estRowItem}
          styleBox={{ flex: 1 }}
          title="キャッシュバック"
          styleTitle={{ fontSize: 10, letterSpacing: -1 }}
          content={!isPriceLabel(cashback) ? cashback : undefined}
        >
          {isPriceLabel(cashback) ? renderPriceWithYen(cashback) : null}
        </TableColPdf>
      </View>
      <View style={styles.layoutFooter}>
        {quotation.midt_jan_cd ? <BarcodePdf value={quotation.midt_jan_cd || ""} /> : <Text style={styles.textFooter}>&nbsp;</Text>}
        <Text style={styles.textFooter}>備考 {quotation.remarks || "-"}</Text>
      </View>
    </View>
  );
}
